// Canvas-based video renderer for chat story export
// Draws frames at 1080x1920 (9:16 vertical) for TikTok/Reels

const VIDEO_WIDTH = 1080;
const VIDEO_HEIGHT = 1920;
const PADDING = 60;
const MESSAGE_PADDING_H = 24;
const MESSAGE_PADDING_V = 16;
const BUBBLE_RADIUS = 36;
const AVATAR_SIZE = 64;
const FONT_SIZE = 34;
const NAME_FONT_SIZE = 24;
const LINE_HEIGHT = FONT_SIZE + 8;
const BG_COLOR = "#1a1a2e";
const RECEIVED_BG = "rgba(255,255,255,0.12)";
const SENT_BG = "#0D6EFD";
const TEXT_COLOR = "#ffffff";
const HEADER_HEIGHT = 120;
const WATERMARK_TEXT = "Made with TinyFiction.com";
const FONT_FAMILY = "-apple-system, BlinkMacSystemFont, sans-serif";
const MESSAGE_GAP = 6;
const GROUP_GAP = 16;

export interface VideoMessage {
  text: string;
  characterName?: string;
  characterId?: string;
  isReceiver: boolean;
  isNarrative: boolean;
  isImage: boolean;
  imageSrc?: string;
}

export class VideoRenderer {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;

  constructor() {
    this.canvas = document.createElement("canvas");
    this.canvas.width = VIDEO_WIDTH;
    this.canvas.height = VIDEO_HEIGHT;
    this.ctx = this.canvas.getContext("2d")!;
  }

  getCanvas(): HTMLCanvasElement {
    return this.canvas;
  }

  drawBackground(): void {
    this.ctx.fillStyle = BG_COLOR;
    this.ctx.fillRect(0, 0, VIDEO_WIDTH, VIDEO_HEIGHT);
  }

  drawHeader(title: string): void {
    this.ctx.fillStyle = "rgba(255,255,255,0.08)";
    this.ctx.fillRect(0, 0, VIDEO_WIDTH, HEADER_HEIGHT);

    this.ctx.fillStyle = "#ffffff";
    this.ctx.font = `bold 36px ${FONT_FAMILY}`;
    this.ctx.textAlign = "center";
    this.ctx.textBaseline = "middle";
    this.ctx.fillText(title, VIDEO_WIDTH / 2, HEADER_HEIGHT / 2, VIDEO_WIDTH - 2 * PADDING);
  }

  drawWatermark(): void {
    this.ctx.fillStyle = "rgba(255,255,255,0.25)";
    this.ctx.font = `500 22px ${FONT_FAMILY}`;
    this.ctx.textAlign = "center";
    this.ctx.textBaseline = "bottom";
    this.ctx.fillText(WATERMARK_TEXT, VIDEO_WIDTH / 2, VIDEO_HEIGHT - 40);
  }

  drawTypingIndicator(
    yPos: number,
    isReceiver: boolean,
    animFrame: number
  ): void {
    const dotSize = 12;
    const spacing = 18;
    const bubbleW = 3 * dotSize + 4 * spacing;
    const bubbleH = dotSize + 2 * MESSAGE_PADDING_V + 8;

    const bubbleX = isReceiver
      ? VIDEO_WIDTH - PADDING - bubbleW
      : PADDING + AVATAR_SIZE + 16;

    this.ctx.fillStyle = isReceiver ? SENT_BG : RECEIVED_BG;
    this.roundRect(bubbleX, yPos, bubbleW, bubbleH, BUBBLE_RADIUS);
    this.ctx.fill();

    for (let i = 0; i < 3; i++) {
      const bounce = Math.sin((animFrame + i * 8) * 0.15) * 6;
      const dotY = yPos + bubbleH / 2 + bounce;
      this.ctx.beginPath();
      this.ctx.fillStyle = isReceiver
        ? "rgba(255,255,255,0.7)"
        : "rgba(255,255,255,0.5)";
      this.ctx.arc(
        bubbleX + spacing + i * (dotSize + spacing) + dotSize / 2,
        dotY,
        dotSize / 2,
        0,
        Math.PI * 2
      );
      this.ctx.fill();
    }
  }

  // Measure message height without drawing
  measureMessage(
    message: VideoMessage,
    showName: boolean
  ): number {
    let height = 0;

    if (message.isNarrative) {
      this.ctx.font = `500 ${NAME_FONT_SIZE}px ${FONT_FAMILY}`;
      const lines = this.wrapText(message.text, VIDEO_WIDTH - 4 * PADDING);
      height = lines.length * (NAME_FONT_SIZE + 6) + 20;
      return height;
    }

    if (showName && message.characterName) {
      height += NAME_FONT_SIZE + 8;
    }

    const maxBubbleWidth = VIDEO_WIDTH * 0.65;
    this.ctx.font = `${FONT_SIZE}px ${FONT_FAMILY}`;
    const lines = this.wrapText(message.text, maxBubbleWidth - 2 * MESSAGE_PADDING_H);
    height += lines.length * LINE_HEIGHT + 2 * MESSAGE_PADDING_V;

    return height;
  }

  drawMessage(
    message: VideoMessage,
    yPos: number,
    showName: boolean,
    showAvatar: boolean,
    avatarImage?: HTMLImageElement
  ): number {
    let currentY = yPos;

    if (message.isNarrative) {
      this.ctx.fillStyle = "rgba(255,255,255,0.45)";
      this.ctx.font = `500 ${NAME_FONT_SIZE}px ${FONT_FAMILY}`;
      this.ctx.textAlign = "center";
      this.ctx.textBaseline = "top";
      const lines = this.wrapText(message.text, VIDEO_WIDTH - 4 * PADDING);
      for (const line of lines) {
        this.ctx.fillText(line, VIDEO_WIDTH / 2, currentY);
        currentY += NAME_FONT_SIZE + 6;
      }
      currentY += 14;
      return currentY;
    }

    // Character name
    if (showName && message.characterName) {
      this.ctx.fillStyle = "rgba(255,255,255,0.45)";
      this.ctx.font = `${NAME_FONT_SIZE}px ${FONT_FAMILY}`;
      this.ctx.textBaseline = "top";
      this.ctx.textAlign = message.isReceiver ? "right" : "left";
      const nameX = message.isReceiver
        ? VIDEO_WIDTH - PADDING - 16
        : PADDING + AVATAR_SIZE + 16;
      this.ctx.fillText(message.characterName, nameX, currentY);
      currentY += NAME_FONT_SIZE + 8;
    }

    // Calculate bubble
    const maxBubbleWidth = VIDEO_WIDTH * 0.65;
    this.ctx.font = `${FONT_SIZE}px ${FONT_FAMILY}`;
    const lines = this.wrapText(message.text, maxBubbleWidth - 2 * MESSAGE_PADDING_H);
    const bubbleHeight = lines.length * LINE_HEIGHT + 2 * MESSAGE_PADDING_V;
    const longestLine = Math.max(
      ...lines.map((l) => this.ctx.measureText(l).width)
    );
    const bubbleWidth = Math.min(
      maxBubbleWidth,
      longestLine + 2 * MESSAGE_PADDING_H
    );

    const avatarSpace = AVATAR_SIZE + 16;
    const bubbleX = message.isReceiver
      ? VIDEO_WIDTH - PADDING - bubbleWidth
      : PADDING + avatarSpace;

    // Avatar
    if (showAvatar) {
      const avatarX = message.isReceiver
        ? VIDEO_WIDTH - PADDING - AVATAR_SIZE
        : PADDING;
      const avatarY = currentY + bubbleHeight - AVATAR_SIZE;

      if (avatarImage) {
        this.ctx.save();
        this.ctx.beginPath();
        this.ctx.arc(
          avatarX + AVATAR_SIZE / 2,
          avatarY + AVATAR_SIZE / 2,
          AVATAR_SIZE / 2,
          0,
          Math.PI * 2
        );
        this.ctx.clip();
        this.ctx.drawImage(avatarImage, avatarX, avatarY, AVATAR_SIZE, AVATAR_SIZE);
        this.ctx.restore();
      } else if (message.characterName) {
        // Placeholder with initials
        this.ctx.fillStyle = "rgba(255,255,255,0.15)";
        this.ctx.beginPath();
        this.ctx.arc(
          avatarX + AVATAR_SIZE / 2,
          avatarY + AVATAR_SIZE / 2,
          AVATAR_SIZE / 2,
          0,
          Math.PI * 2
        );
        this.ctx.fill();

        this.ctx.fillStyle = "#ffffff";
        this.ctx.font = `500 24px ${FONT_FAMILY}`;
        this.ctx.textAlign = "center";
        this.ctx.textBaseline = "middle";
        const initials = message.characterName
          .split(" ")
          .slice(0, 2)
          .map((w) => w[0])
          .join("")
          .toUpperCase();
        this.ctx.fillText(
          initials,
          avatarX + AVATAR_SIZE / 2,
          avatarY + AVATAR_SIZE / 2
        );
      }
    }

    // Bubble
    this.ctx.fillStyle = message.isReceiver ? SENT_BG : RECEIVED_BG;
    this.roundRect(bubbleX, currentY, bubbleWidth, bubbleHeight, BUBBLE_RADIUS);
    this.ctx.fill();

    // Text
    this.ctx.fillStyle = TEXT_COLOR;
    this.ctx.font = `${FONT_SIZE}px ${FONT_FAMILY}`;
    this.ctx.textAlign = "left";
    this.ctx.textBaseline = "top";
    lines.forEach((line, i) => {
      this.ctx.fillText(
        line,
        bubbleX + MESSAGE_PADDING_H,
        currentY + MESSAGE_PADDING_V + i * LINE_HEIGHT
      );
    });

    currentY += bubbleHeight;
    return currentY;
  }

  // Calculate scroll offset so messages stay within visible area
  calculateScrollOffset(
    messages: VideoMessage[],
    visibleIndices: number[],
    showTyping: boolean
  ): number {
    const visibleAreaTop = HEADER_HEIGHT + 20;
    const visibleAreaBottom = VIDEO_HEIGHT - 100; // Leave room for watermark
    const visibleHeight = visibleAreaBottom - visibleAreaTop;

    let totalHeight = 0;
    for (let i = 0; i < visibleIndices.length; i++) {
      const idx = visibleIndices[i];
      const msg = messages[idx];
      const prevMsg = idx > 0 ? messages[idx - 1] : null;
      const showName = !msg.isNarrative && (idx === 0 || msg.characterId !== prevMsg?.characterId);
      const isNewGroup = idx > 0 && msg.characterId !== prevMsg?.characterId;

      if (isNewGroup) totalHeight += GROUP_GAP;
      else if (i > 0) totalHeight += MESSAGE_GAP;

      totalHeight += this.measureMessage(msg, showName);
    }

    if (showTyping) {
      totalHeight += GROUP_GAP + 60; // typing indicator height
    }

    if (totalHeight > visibleHeight) {
      return totalHeight - visibleHeight;
    }
    return 0;
  }

  private wrapText(text: string, maxWidth: number): string[] {
    if (!text) return [""];
    const words = text.split(" ");
    const lines: string[] = [];
    let currentLine = "";

    for (const word of words) {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      if (this.ctx.measureText(testLine).width > maxWidth && currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = testLine;
      }
    }
    if (currentLine) lines.push(currentLine);
    return lines.length > 0 ? lines : [""];
  }

  private roundRect(x: number, y: number, w: number, h: number, r: number): void {
    this.ctx.beginPath();
    this.ctx.moveTo(x + r, y);
    this.ctx.arcTo(x + w, y, x + w, y + h, r);
    this.ctx.arcTo(x + w, y + h, x, y + h, r);
    this.ctx.arcTo(x, y + h, x, y, r);
    this.ctx.arcTo(x, y, x + w, y, r);
    this.ctx.closePath();
  }
}
